import type { OidcLauncher } from '@/ports/oidc-launcher'

/**
 * BrowserOidcLauncher : top-level navigation vers `/auth/oidc/login` du
 * backend. Le navigateur enchaîne ensuite IDP → callback backend →
 * redirect interne avec cookie de session déposé.
 *
 * Pourquoi `window.location.assign` plutôt que `<router-link>` :
 * - L'IDP fait un POST/GET cross-origin → on doit sortir du SPA
 *   (top-level navigation, pas un fetch XHR).
 * - On laisse explicitement le navigateur recharger la page après
 *   callback : le hydrate /auth/me se ré-exécute sur reboot du SPA.
 *
 * Le `redirect` est append en query (`?redirect=/...`). Le backend
 * sanitize (cf. controlplane/auth_oidc.go::sanitizeInternalRedirect),
 * donc inutile de re-valider ici — mais on encode quand même pour ne
 * pas casser des paths contenant `?` ou `&`.
 *
 * `navigate` et `origin` sont injectables pour rendre le launcher
 * testable : jsdom refuse de redéfinir `window.location.assign`, donc
 * on évite d'y toucher en spy. En prod, les defaults pointent vers la
 * vraie `window`.
 */
export class BrowserOidcLauncher implements OidcLauncher {
    private readonly navigate: (url: string) => void
    private readonly origin: string

    constructor(
        navigate: (url: string) => void = (url) => window.location.assign(url),
        origin: string = window.location.origin,
    ) {
        this.navigate = navigate
        this.origin = origin
    }

    start(redirect: string): void {
        const url = new URL('/auth/oidc/login', this.origin)
        if (redirect) {
            url.searchParams.set('redirect', redirect)
        }
        this.navigate(url.toString())
    }
}
